// @ts-strict-ignore
import {
    CreateIntegrationRequest,
    CreateIntegrationRequestDataType,
    tryCreateIntegrationRequest,
} from '@/actions/integrations';
import { StepConfigUpdatable, UpdateStepConfigRequest } from '@/actions/stepConfig';
import IntegrationsForCapabilityComponent from '@/components/IntegrationsForCapabilityComponent';
import {
    getIntegrationSettingsByArchetype,
    getIntegrationSpecificSettingsFields,
    getStepConfigSettingsInitialValuesByArchetype,
    getStepConfigSettingsFields,
    IntegrationSettingsByArchetype,
    StepConfigSettingsInitialValuesByArchetype,
    transformStepConfigSettingsToUpdateRequestValues,
} from '@/components/stepConfigs/stepConfigSettingsForms';
import { FetchableEntityTypeV2 } from '@/entityType';
import {
    allSupportedIntegrationTypesForCapability,
    displayNameForCapability,
    getError,
    getIsLoading,
    getUpdated,
} from '@/helpers';
import { renderFormFields } from '@/helpers/formHelpers';
import {
    authTypeForIntegrationType,
    capabilitySupportsMultipleInstallationsPerStep,
    displayNameForIntegrationType,
    iconAndAltForIntegrationType,
    integrationUpdatableForIntegrationType,
} from '@/helpers/integrationHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectAppIntegrations,
    selectBranchConfigsById,
    selectConnectedIntegrationsForStepConfig,
    selectFeatureAffiliationConfigsById,
    selectIntegrationFromIntegrationType,
    selectIsAppsDemoModeEnabled,
    selectIsRunwayOrg,
    selectStepConfig,
    selectTagConfigsById,
    selectUserIsRunwayAdmin,
} from '@/selectors/selectors';
import { StepConfig } from '@/types/stepConfig';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable, useGetEntityV2 } from '@/utils/hooks/helpers';
import { ApiOutlined, CopyOutlined, SettingOutlined } from '@ant-design/icons';
import Badge from 'antd/lib/badge';
import Alert from 'antd/lib/alert/Alert';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Descriptions from 'antd/lib/descriptions';
import Divider from 'antd/lib/divider';
import Drawer from 'antd/lib/drawer';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Menu from 'antd/lib/menu';
import Select, { SelectProps } from 'antd/lib/select';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    AllIntegrationCapabilities,
    AllIntegrationTypes,
    BranchConfig,
    FeatureAffiliationConfig,
    Integration,
    IntegrationCapability,
    IntegrationErrorType,
    IntegrationType,
    ReleaseStepType,
    RunwayResponseType,
    TagConfig,
} from '../../types';
import IntegrationConfiguration from '../IntegrationConfiguration';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { useOauthIntegrationInstaller } from '../setup/hooks';
import { useUpdateIntegrationModal } from '../setup/useUpdateIntegrationModal';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import Dropdown from 'antd/lib/dropdown';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { ExtendedForm, ExtendedFormObject, ExtendedFormState } from '@/utils/ExtendedForm';
import { omit } from 'lodash';
import { MenuItemType } from 'antd/lib/menu/interface';
import { useExtendedForm } from '@/utils/hooks/useExtendedForm';
import Flex from 'antd/lib/flex';

export enum MenuKey {
    Settings = 'Settings',
    Integrations = 'Integrations',
    Schedule = 'Schedule',
    Automations = 'Automations',
    Checklists = 'Checklists',
}

const getButtonTextForMenuKey = (menuKey: MenuKey): string => {
    switch (menuKey) {
        case MenuKey.Settings:
            return 'Save settings';
        case MenuKey.Integrations:
            return 'Save integrations';
        case MenuKey.Schedule:
            return 'Save schedule';
        case MenuKey.Automations:
            return 'Save automations';
        case MenuKey.Checklists:
            return 'Save checklists';
        default:
            return 'Save settings';
    }
};

const getMenuItemBadgeState = (
    menuKey: MenuKey,
    formStatesById: Partial<Record<MenuKey, ExtendedFormState[]>>,
): { showBadge: boolean; color: 'error' | 'warning'; tooltipText: string } | null => {
    const formsForMenuKey = formStatesById[menuKey];

    const hasInvalidForms = formsForMenuKey.some((form) => form.isInvalid);
    const hasDirtyForms = formsForMenuKey.some((form) => form.isDirty);

    if (hasInvalidForms) {
        return {
            showBadge: true,
            color: 'error',
            tooltipText: 'This tab has validation errors that need to be addressed.',
        };
    } else if (hasDirtyForms) {
        return {
            showBadge: true,
            color: 'warning',
            tooltipText: 'This tab has unsaved changes.',
        };
    }

    return null;
};

const createMenuItemWithBadge = (
    currentMenuKey: MenuKey,
    menuKey: MenuKey,
    icon: React.ReactNode,
    formStatesById: Partial<Record<MenuKey, ExtendedFormState[]>>,
): MenuItemType => {
    const isSelected = currentMenuKey === menuKey;
    const badgeState = getMenuItemBadgeState(menuKey, formStatesById);

    const labelContent = (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
                {icon}

                <Text style={{ color: isSelected ? runwayColors.primary : antGrey.grey9 }}>{menuKey}</Text>
            </Space>
            {badgeState?.showBadge && (
                <Tooltip title={badgeState.tooltipText}>
                    <Badge status={badgeState.color} />
                </Tooltip>
            )}
        </Space>
    );

    return {
        label: labelContent,
        key: menuKey,
    };
};

export interface StepRunSettingsDrawerProps {
    appId: string;
    orgId: string;
    stepConfigId: string;
    workflowId: string;
    originStepRunId?: string;
    isOriginReleaseActive?: boolean;
    releaseStepTypeText: string;
    isInsideStep?: boolean;
    isOpen: boolean;
    stepName?: string;
    onClose?: () => void;
}

type StepConfigGeneralSettingsFormType = Pick<
    StepConfig,
    'name' | 'branchConfigId' | 'tagConfigId' | 'featureAffiliationConfigId'
>;

export const StepConfigSettingsDrawer: React.FC<StepRunSettingsDrawerProps> = ({
    appId,
    orgId,
    stepConfigId,
    workflowId,
    originStepRunId,
    isOriginReleaseActive = false,
    releaseStepTypeText,
    stepName,
    isOpen,
    isInsideStep,
    onClose,
}) => {
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const [getStepConfig, getStepConfigIsLoading, getStepConfigError] = useGetEntityV2(
        FetchableEntityTypeV2.StepConfig,
        stepConfigId,
    );

    const stepConfig = useSelector((state: RootState) => selectStepConfig(state, stepConfigId));

    React.useEffect(() => {
        if (getStepConfigError) {
            globalMessage.error('Failed to load step settings');
        }
    }, [getStepConfigError]);

    React.useEffect(() => {
        if (isOpen && !isViewingAppsInDemoMode) {
            getStepConfig({
                workflowId,
                stepConfigId,
                orgId,
            });
        }
    }, [isOpen, isViewingAppsInDemoMode, stepConfigId, workflowId, orgId]);

    // to intercept oauth installation redirects
    useOauthIntegrationInstaller({});

    const [activeMenuKey, setActiveMenuKey] = React.useState<MenuKey>(MenuKey.Settings);

    const history = useHistory();
    const lastInstalledIntegrationType = history.location.state?.['lastInstalledIntegrationType'];
    const lastInstalledIntegration = useSelector((state: RootState) =>
        selectIntegrationFromIntegrationType(state, appId, lastInstalledIntegrationType),
    );

    React.useEffect(() => {
        if (lastInstalledIntegration) {
            setSelectedIntegration(lastInstalledIntegration);
        }
    }, [lastInstalledIntegration]);

    const allAppIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));

    const [selectedIntegration, setSelectedIntegration] = React.useState<Integration | null>(null);

    //handles configuring existing integration
    const { setShowUpdateIntegrationModal, updateIntegrationModalProps } = useUpdateIntegrationModal({
        orgId,
        appId,
        selectedIntegration,
        closable: false,
    });

    const addIntegrationFailed = useSelector((state: RootState) => {
        const errorState = {};
        AllIntegrationTypes.forEach((integrationType) => {
            const authType = authTypeForIntegrationType(integrationType);
            if (authType) {
                errorState[integrationType] = getError(authType, state);
            }
        });

        allAppIntegrations.forEach((integration) => {
            const authType = authTypeForIntegrationType(integration.id);
            if (authType) {
                errorState[integration.installationId] = getError(authType, state, integration.installationId);
            }
        });

        return errorState;
    }, shallowEqual);

    const updateIntegrationSucceeded = useSelector((state: RootState) => {
        const successState = {};
        allAppIntegrations.forEach((integration) => {
            const updateType = integrationUpdatableForIntegrationType(integration.id);
            if (updateType) {
                successState[integration.installationId] = getUpdated(updateType, state, integration.installationId);
            }
        });

        return successState;
    }, shallowEqual);

    const updateIntegrationFailed = useSelector((state: RootState) => {
        const errorState = {};
        allAppIntegrations.forEach((integration) => {
            const updateType = integrationUpdatableForIntegrationType(integration.id);
            if (updateType) {
                errorState[integration.installationId] = getError(updateType, state, integration.installationId);
            }
        });
        return errorState;
    }, shallowEqual);

    // Add integration loading and success state
    const addIntegrationIsLoading = useSelector((state: RootState) => {
        const loadingState = {};
        AllIntegrationTypes.forEach((integrationType) => {
            const authType = authTypeForIntegrationType(integrationType);
            if (authType) {
                loadingState[integrationType] = getIsLoading(authType, state);
            }
        });
        return loadingState;
    }, shallowEqual);

    const addIntegrationSucceeded = useSelector((state: RootState) => {
        const successState = {};
        AllIntegrationTypes.forEach((integrationType) => {
            const authType = authTypeForIntegrationType(integrationType);
            if (authType) {
                successState[integrationType] = getUpdated(authType, state);
            }
        });

        allAppIntegrations.forEach((integration) => {
            const authType = authTypeForIntegrationType(integration.id);
            if (authType) {
                const updated = getUpdated(authType, state, integration.installationId);
                successState[integration.installationId] = updated;
            }
        });

        return successState;
    }, shallowEqual);

    // Handle integration status changes
    React.useEffect(() => {
        // New integration errors
        Object.entries(addIntegrationFailed).forEach(([_, errorMessage]) => {
            if (errorMessage) {
                globalMessage.error(String(errorMessage));
            }
        });

        // New integration success
        Object.entries(addIntegrationSucceeded).forEach(([integrationId, updated]) => {
            if (updated) {
                const integration = allAppIntegrations.find(
                    (integration) => integration.installationId === integrationId,
                );

                if (integration && !integration.integrationError) {
                    const integrationDisplayName = displayNameForIntegrationType(integration.id);
                    globalMessage.success(`${integrationDisplayName} integration added!`);
                } else if (
                    integration &&
                    integration.integrationError?.status === IntegrationErrorType.IncompleteData
                ) {
                    setSelectedIntegration(integration);
                    setShowUpdateIntegrationModal(true);
                } else if (integration && integration.integrationError) {
                    globalMessage.error(integration.integrationError.message);
                } else {
                    globalMessage.error('Something went wrong, please try again');
                }
            }
        });

        // Update integration failure
        Object.entries(updateIntegrationFailed).forEach(([_, errorMessage]) => {
            if (errorMessage) {
                globalMessage.error(String(errorMessage));
            }
        });

        // Update integration success
        Object.entries(updateIntegrationSucceeded).forEach(([_, updated]) => {
            if (updated) {
                setSelectedIntegration(null);
                setShowUpdateIntegrationModal(false);
            }
        });
    }, [addIntegrationFailed, addIntegrationSucceeded, updateIntegrationFailed, updateIntegrationSucceeded]);

    const [updateStepConfig, updateStepConfigIsLoading, updateStepConfigResponseType, updateStepConfigError] =
        useCallUpdatable(StepConfigUpdatable.UpdateStepConfig, {
            workflowId,
            orgId,
            stepConfigId,
        });

    const destinations = stepConfig?.destinations;

    const [stepConfigGeneralSettingsExtendedFormState, stepConfigGeneralSettingsExtendedForm] =
        useExtendedForm<StepConfigGeneralSettingsFormType>();
    const [stepConfigArchetypeSpecificSettingsExtendedFormState, stepConfigArchetypeSpecificSettingsExtendedForm] =
        useExtendedForm<StepConfigArchetypeSpecificSettingsFormType>();
    const [integrationsExtendedFormState, integrationsExtendedForm] = useExtendedForm<IntegrationsFormValues>();
    const didHandleSaveSuccessRef = React.useRef(false);

    React.useEffect(() => {
        const didSucceed = updateStepConfigResponseType === RunwayResponseType.Success;
        if (didSucceed && !didHandleSaveSuccessRef.current) {
            didHandleSaveSuccessRef.current = true;
            globalMessage.success('Step settings updated successfully');
        }

        if (!didSucceed) {
            didHandleSaveSuccessRef.current = false;
        }
    }, [updateStepConfigResponseType]);

    React.useEffect(() => {
        if (updateStepConfigError) {
            globalMessage.error(updateStepConfigError);
        }
    }, [updateStepConfigError]);

    const formStatesById = {
        [MenuKey.Settings]: [
            stepConfigGeneralSettingsExtendedFormState,
            stepConfigArchetypeSpecificSettingsExtendedFormState,
        ],
        [MenuKey.Integrations]: [integrationsExtendedFormState],
    };

    const areAllFormsPristine = Object.values(formStatesById).every((formStates) =>
        formStates.every((formState) => !formState),
    );
    const isAnyFormInvalid = Object.values(formStatesById).some((formStates) =>
        formStates.some((formState) => formState.isInvalid),
    );

    const isCurrentFormInvalid = formStatesById[activeMenuKey]?.some((formState) => formState.isInvalid) ?? false;
    const isCurrentFormDirty = formStatesById[activeMenuKey]?.some((formState) => formState.isDirty) ?? false;

    const isCurrentFormPristine = !isCurrentFormDirty;

    return (
        <>
            {selectedIntegration && (
                <IntegrationConfiguration
                    {...updateIntegrationModalProps}
                    onClose={() => {
                        setShowUpdateIntegrationModal(false);
                        setSelectedIntegration(null);
                    }}
                />
            )}
            <Drawer
                destroyOnClose
                title={
                    <TopBar
                        releaseStepTypeText={releaseStepTypeText}
                        stepName={stepName}
                        isCurrentFormInvalid={isCurrentFormInvalid}
                        isCurrentFormPristine={isCurrentFormPristine}
                        isViewingAppsInDemoMode={isViewingAppsInDemoMode}
                        areAllFormsPristine={areAllFormsPristine}
                        isAnyFormInvalid={isAnyFormInvalid}
                        updateStepConfigIsLoading={updateStepConfigIsLoading}
                        activeMenuKey={activeMenuKey}
                        destinations={destinations}
                        onSaveClick={async (saveAll: boolean) => {
                            try {
                                const requestData: UpdateStepConfigRequest = {};

                                if (originStepRunId) {
                                    requestData.originStepRunId = originStepRunId;
                                }

                                const menuKeysToSave =
                                    saveAll ? [MenuKey.Settings, MenuKey.Integrations] : [activeMenuKey];

                                for (const menuKey of menuKeysToSave) {
                                    switch (menuKey) {
                                        case MenuKey.Settings: {
                                            const stepConfigGeneralSettingsFormValues =
                                                await stepConfigGeneralSettingsExtendedForm.form.validateFields();
                                            const stepConfigArchetypeSpecificSettingsFormValues =
                                                await stepConfigArchetypeSpecificSettingsExtendedForm.form.validateFields();

                                            requestData.stepConfigGeneralSettings = {
                                                name: stepConfigGeneralSettingsFormValues.name,
                                            };
                                            requestData.stepConfigArchetypeSpecificSettings =
                                                transformStepConfigSettingsToUpdateRequestValues(
                                                    stepConfig.archetype,
                                                    stepConfigArchetypeSpecificSettingsFormValues,
                                                    stepConfigGeneralSettingsFormValues.branchConfigId,
                                                    stepConfigGeneralSettingsFormValues.tagConfigId,
                                                    stepConfigGeneralSettingsFormValues.featureAffiliationConfigId,
                                                );
                                            break;
                                        }
                                        case MenuKey.Integrations: {
                                            const integrationsFormValues =
                                                await integrationsExtendedForm.form.validateFields();
                                            requestData.integrationSpecificSettings =
                                                transformStepConfigSettingsToUpdateRequestValues(
                                                    stepConfig.archetype,
                                                    integrationsFormValues.integrationSpecificSettings as unknown as StepConfigSettingsInitialValuesByArchetype<ReleaseStepType>,
                                                    null,
                                                    null,
                                                    null,
                                                );
                                            const requestedIds = Object.values<string>(
                                                integrationsFormValues.integrationInstallationIdByCapability,
                                            ).filter((value) => value);
                                            const mergedIds = new Set<string>(requestedIds);
                                            (stepConfig.integrationInstallationIds ?? []).forEach((installationId) => {
                                                const integration = allAppIntegrations.find(
                                                    (i) => i.installationId === installationId,
                                                );
                                                if (!integration) return;
                                                if (
                                                    integration.capabilities.some(
                                                        capabilitySupportsMultipleInstallationsPerStep,
                                                    )
                                                ) {
                                                    mergedIds.add(installationId);
                                                }
                                            });
                                            requestData.integrationInstallationIds = Array.from(mergedIds);
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                }

                                updateStepConfig(requestData);
                            } catch (error) {
                                console.error('error saving step config', error);
                                globalMessage.error('Please correct all errors before saving');
                            }
                        }}
                    />
                }
                closeIcon={false}
                width={1024}
                open={isOpen}
                onClose={onClose}
                styles={{
                    header: {
                        padding: 0,
                    },
                    body: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                    },
                }}
            >
                {getStepConfigIsLoading ?
                    <Spin
                        size="large"
                        spinning={getStepConfigIsLoading}
                        style={{
                            alignSelf: 'center',
                        }}
                    />
                :   <>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                paddingTop: 24,
                                alignSelf: 'stretch',
                                flex: 1,
                                maxWidth: '100%',
                            }}
                        >
                            <Menu
                                style={{
                                    width: 208,
                                }}
                                onClick={(menuInfo) => {
                                    setActiveMenuKey(menuInfo.key as MenuKey);
                                }}
                                selectedKeys={[activeMenuKey]}
                                mode="vertical"
                                disabled={updateStepConfigIsLoading}
                                items={[
                                    createMenuItemWithBadge(
                                        activeMenuKey,
                                        MenuKey.Settings,
                                        <SettingOutlined />,
                                        formStatesById,
                                    ),
                                    createMenuItemWithBadge(
                                        activeMenuKey,
                                        MenuKey.Integrations,
                                        <ApiOutlined />,
                                        formStatesById,
                                    ),
                                    // {
                                    //     label: MenuKey.Schedule,
                                    //     key: MenuKey.Schedule,
                                    //     icon: <CalendarOutlined />,
                                    // },
                                    // {
                                    //     label: MenuKey.Automations,
                                    //     key: MenuKey.Automations,
                                    //     icon: <ThunderboltOutlined />,
                                    // },
                                    // {
                                    //     label: MenuKey.Checklists,
                                    //     key: MenuKey.Checklists,
                                    //     icon: <UnorderedListOutlined />,
                                    // },
                                ]}
                            />
                            {stepConfig && (
                                <div
                                    style={{
                                        flex: 1,
                                        paddingRight: 24,
                                        paddingLeft: 24,
                                        overflowY: 'auto',
                                    }}
                                >
                                    {isInsideStep && (
                                        <Alert
                                            showIcon
                                            type="warning"
                                            message={
                                                isOriginReleaseActive ?
                                                    'Settings saved here will apply to all active releases (including this one) and all future releases.'
                                                :   "Settings saved here will apply to any active and future releases. The current release is no longer active and won't be updated."
                                            }
                                            style={{ marginBottom: 24 }}
                                        />
                                    )}
                                    <Settings
                                        hidden={activeMenuKey !== MenuKey.Settings}
                                        appId={appId}
                                        stepConfig={stepConfig}
                                        isInsideStep={isInsideStep}
                                        isDisabled={updateStepConfigIsLoading}
                                        stepConfigGeneralSetting={stepConfigGeneralSettingsExtendedForm}
                                        extendedFormObjectForArchetypeSpecificSettings={
                                            stepConfigArchetypeSpecificSettingsExtendedForm
                                        }
                                    />

                                    <Integrations
                                        hidden={activeMenuKey !== MenuKey.Integrations}
                                        orgId={orgId}
                                        appId={appId}
                                        stepConfig={stepConfig}
                                        addIntegrationIsLoading={addIntegrationIsLoading}
                                        addIntegrationSucceeded={addIntegrationSucceeded}
                                        isDisabled={updateStepConfigIsLoading}
                                        extendedFormObject={integrationsExtendedForm}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                }
            </Drawer>
        </>
    );
};

type StepConfigArchetypeSpecificSettingsFormType = StepConfigSettingsInitialValuesByArchetype<ReleaseStepType>;

const TopBar: React.FC<{
    releaseStepTypeText: string;
    stepName?: string;
    isCurrentFormInvalid: boolean;
    isCurrentFormPristine: boolean;
    isViewingAppsInDemoMode: boolean;
    areAllFormsPristine: boolean;
    isAnyFormInvalid: boolean;
    updateStepConfigIsLoading: boolean;
    activeMenuKey: MenuKey;
    destinations: Destination[];
    onSaveClick: (saveAll: boolean) => void;
}> = ({
    stepName,
    isCurrentFormInvalid,
    isCurrentFormPristine,
    isViewingAppsInDemoMode,
    areAllFormsPristine,
    isAnyFormInvalid,
    updateStepConfigIsLoading,
    activeMenuKey,
    onSaveClick,
    destinations,
}) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 76,
                width: '100%',
                paddingRight: 24,
                paddingLeft: 36,
                flexGrow: 0,
                flexShrink: 0,
                backgroundColor: antGrey.grey3,
            }}
        >
            <Space
                split={
                    <Divider
                        type="vertical"
                        style={{
                            backgroundColor: antGrey.grey5,
                            height: 22,
                        }}
                    />
                }
            >
                <Text
                    style={{
                        ...titleStyle,
                        fontSize: 18,
                    }}
                >
                    {stepName}
                </Text>
                {destinations && <DestinationCounter destinations={destinations} />}
            </Space>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Dropdown.Button
                    type="primary"
                    disabled={
                        isCurrentFormPristine ||
                        isCurrentFormInvalid ||
                        updateStepConfigIsLoading ||
                        isViewingAppsInDemoMode
                    }
                    loading={updateStepConfigIsLoading}
                    onClick={() => onSaveClick(false)}
                    menu={{
                        items: [
                            {
                                disabled: areAllFormsPristine || isAnyFormInvalid || updateStepConfigIsLoading,
                                key: 'save',
                                label: 'Save everything',
                                onClick: () => onSaveClick(true),
                            },
                        ],
                    }}
                >
                    {getButtonTextForMenuKey(activeMenuKey)}
                </Dropdown.Button>
            </div>
        </div>
    );
};

const Settings: React.FC<{
    appId: string;
    stepConfig: StepConfig;
    hidden: boolean;
    isInsideStep?: boolean;
    isDisabled?: boolean;
    stepConfigGeneralSetting: ExtendedFormObject<StepConfigGeneralSettingsFormType>;
    extendedFormObjectForArchetypeSpecificSettings: ExtendedFormObject<StepConfigArchetypeSpecificSettingsFormType>;
}> = ({
    stepConfig,
    appId,
    hidden,
    isInsideStep,
    isDisabled,
    stepConfigGeneralSetting,
    extendedFormObjectForArchetypeSpecificSettings,
}) => {
    const branchConfigs = useSelector((state: RootState) => selectBranchConfigsById(state, appId));
    const tagConfigs = useSelector((state: RootState) => selectTagConfigsById(state, appId));
    const featureAffiliationConfigs = useSelector((state: RootState) =>
        selectFeatureAffiliationConfigsById(state, appId),
    );

    const selectedBranchConfigId = Form.useWatch('branchConfigId', stepConfigGeneralSetting.form);
    const selectedTagConfigId = Form.useWatch('tagConfigId', stepConfigGeneralSetting.form);
    const selectedFeatureAffiliationConfigId = Form.useWatch(
        'featureAffiliationConfigId',
        stepConfigGeneralSetting.form,
    );

    const selectedBranchConfig = branchConfigs[selectedBranchConfigId];
    const selectedTagConfig = tagConfigs[selectedTagConfigId];
    const selectedFeatureAffiliationConfig = featureAffiliationConfigs[selectedFeatureAffiliationConfigId];

    const initialValuesForSettings: Omit<
        StepConfigSettingsInitialValuesByArchetype<typeof stepConfig.archetype>,
        'googleTestingTrackName'
    > = omit(getStepConfigSettingsInitialValuesByArchetype(stepConfig), 'googleTestingTrackName');

    const stepConfigSettingsFields = getStepConfigSettingsFields({
        appId,
        stepConfig,
        isEditable: !isDisabled,
        shouldAddDefaultWarningText: !isInsideStep,
    });

    const copyToClipboard = async (id) => {
        try {
            await navigator.clipboard.writeText(id);
            globalMessage.success('Copied to clipboard!');
        } catch {
            globalMessage.error('Failed to copy to clipboard');
        }
    };

    const hotfixBranchPatterns =
        selectedBranchConfig?.releaseBranchPatterns?.filter(
            (branchPattern, index) => branchPattern.isHotfix && index === 0,
        ) || [];
    const nonHotfixBranchPatterns =
        selectedBranchConfig?.releaseBranchPatterns?.filter(
            (branchPattern, index) => !branchPattern.isHotfix && index === 0,
        ) || [];

    const isRunwayAdmin = useSelector((state: RootState) => selectUserIsRunwayAdmin(state));

    const shouldAllowSharedSettingsEditing = !!isRunwayAdmin;

    const hasAnySharedSettings = !!(
        stepConfig.branchConfigId ||
        stepConfig.tagConfigId ||
        stepConfig.featureAffiliationConfigId
    );

    return (
        <>
            <ExtendedForm
                autoComplete="off"
                hidden={hidden}
                extendedFormObject={stepConfigGeneralSetting}
                layout="vertical"
                preserve={false}
                initialValues={{
                    name: stepConfig.name,
                    branchConfigId: stepConfig.branchConfigId,
                    tagConfigId: stepConfig.tagConfigId,
                    featureAffiliationConfigId: stepConfig.featureAffiliationConfigId,
                }}
                disabled={isDisabled}
            >
                <Form.Item
                    label="Step name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter a step name' }]}
                >
                    <Input />
                </Form.Item>
                <Space>
                    <Text type="secondary">Step ID: </Text>
                    <Text code>{stepConfig.id}</Text>
                    <Button
                        onClick={() => copyToClipboard(stepConfig.id)}
                        icon={<CopyOutlined style={{ color: runwayColors.primary }} />}
                        type="link"
                    />
                </Space>

                {hasAnySharedSettings && (
                    <>
                        <Divider />
                        <Text
                            style={{
                                ...titleStyle,
                                fontWeight: 400,
                                fontSize: 14,
                                marginBottom: 16,
                                display: 'block',
                            }}
                        >
                            Shared settings
                        </Text>

                        <Flex vertical gap={16}>
                            <Form.Item
                                hidden={!shouldAllowSharedSettingsEditing || !stepConfig.branchConfigId}
                                label="Branches"
                                name="branchConfigId"
                                required
                                rules={[{ required: true, message: 'Please select a branch' }]}
                            >
                                <Select
                                    options={Object.entries(branchConfigs).map(
                                        ([branchConfigId, branchConfig]: [string, BranchConfig]) => ({
                                            label: branchConfig.id,
                                            value: branchConfigId,
                                        }),
                                    )}
                                />
                            </Form.Item>
                            {selectedBranchConfig && (
                                <Descriptions bordered column={1}>
                                    <Descriptions.Item label="Release branch">
                                        <Space
                                            direction="vertical"
                                            split={
                                                <Divider
                                                    type="horizontal"
                                                    style={{ backgroundColor: antGrey.grey5, width: '100%' }}
                                                />
                                            }
                                        >
                                            {selectedBranchConfig.prodBranch && (
                                                <Text>{selectedBranchConfig.prodBranch}</Text>
                                            )}
                                            {nonHotfixBranchPatterns.length > 0 && (
                                                <Space split={<Divider type="vertical" />}>
                                                    {nonHotfixBranchPatterns.map((branchPattern) => (
                                                        <Text code>{branchPattern.pattern}</Text>
                                                    ))}
                                                </Space>
                                            )}
                                            {hotfixBranchPatterns.length > 0 && (
                                                <Space split={<Divider type="vertical" />}>
                                                    <Text>Hotfix</Text>
                                                    {hotfixBranchPatterns.map((branchPattern) => (
                                                        <Text code>{branchPattern.pattern}</Text>
                                                    ))}
                                                </Space>
                                            )}
                                        </Space>
                                    </Descriptions.Item>

                                    {selectedBranchConfig.workingBranch && (
                                        <Descriptions.Item label="Working branch">
                                            <Text>{selectedBranchConfig.workingBranch}</Text>
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                            )}

                            <Form.Item
                                label="Tag"
                                name="tagConfigId"
                                required
                                hidden={!shouldAllowSharedSettingsEditing || !stepConfig.tagConfigId}
                            >
                                <Select
                                    options={Object.entries(tagConfigs).map(
                                        ([tagConfigId, tagConfig]: [string, TagConfig]) => ({
                                            label: tagConfig.id,
                                            value: tagConfigId,
                                        }),
                                    )}
                                />
                            </Form.Item>

                            {selectedTagConfig && (
                                <Descriptions bordered column={1}>
                                    <Descriptions.Item label="Release tag pattern">
                                        <Space>
                                            <Text code>{selectedTagConfig.pattern}</Text>
                                        </Space>
                                    </Descriptions.Item>
                                </Descriptions>
                            )}

                            <Form.Item
                                label="Feature affiliation"
                                name="featureAffiliationConfigId"
                                required
                                hidden={!shouldAllowSharedSettingsEditing || !stepConfig.featureAffiliationConfigId}
                            >
                                <Select
                                    options={Object.entries(featureAffiliationConfigs).map(
                                        ([featureAffiliationConfigId, featureAffiliationConfig]: [
                                            string,
                                            FeatureAffiliationConfig,
                                        ]) => ({
                                            label: featureAffiliationConfig.id,
                                            value: featureAffiliationConfigId,
                                        }),
                                    )}
                                />
                            </Form.Item>

                            {selectedFeatureAffiliationConfig && (
                                <Descriptions bordered column={1}>
                                    <Descriptions.Item
                                        label={
                                            <Space split={<Divider type="vertical" />}>
                                                <Text>{selectedFeatureAffiliationConfig.type}</Text>
                                                <Text code>{selectedFeatureAffiliationConfig.pattern}</Text>
                                            </Space>
                                        }
                                    >
                                        <img
                                            src={
                                                iconAndAltForIntegrationType(
                                                    selectedFeatureAffiliationConfig.integrationId,
                                                )[0]
                                            }
                                            alt={
                                                iconAndAltForIntegrationType(
                                                    selectedFeatureAffiliationConfig.integrationId,
                                                )[1]
                                            }
                                            width={20}
                                            height={20}
                                        />

                                        <Text>
                                            {displayNameForIntegrationType(
                                                selectedFeatureAffiliationConfig.integrationId,
                                            )}
                                        </Text>
                                    </Descriptions.Item>
                                </Descriptions>
                            )}
                        </Flex>
                    </>
                )}
            </ExtendedForm>
            <ExtendedForm
                hidden={hidden || stepConfigSettingsFields.length === 0}
                extendedFormObject={extendedFormObjectForArchetypeSpecificSettings}
                layout="vertical"
                autoComplete="off"
                preserve={false}
                initialValues={initialValuesForSettings}
                disabled={isDisabled}
            >
                <Divider />
                <Text
                    style={{
                        ...titleStyle,
                        fontWeight: 400,
                        fontSize: 14,
                        marginBottom: 16,
                        display: 'block',
                    }}
                >
                    Step settings
                </Text>

                {renderFormFields(stepConfigSettingsFields)}
            </ExtendedForm>
        </>
    );
};

interface IntegrationsFormValues {
    integrationSpecificSettings: IntegrationSettingsByArchetype<ReleaseStepType>;
    integrationInstallationIdByCapability: Record<string, string>;
}

const Integrations: React.FC<{
    orgId: string;
    appId: string;
    stepConfig: StepConfig;
    addIntegrationIsLoading: Partial<Record<IntegrationType, boolean>>;
    addIntegrationSucceeded: Partial<Record<IntegrationType, boolean>>;
    hidden: boolean;
    isDisabled?: boolean;
    extendedFormObject: ExtendedFormObject<IntegrationsFormValues>;
}> = ({
    stepConfig,
    appId,
    orgId,
    addIntegrationIsLoading,
    addIntegrationSucceeded,
    hidden,
    isDisabled,
    extendedFormObject,
}) => {
    const dispatch = useDispatch();

    const allAppIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));

    const allAppIntegrationsByCapability = AllIntegrationCapabilities.reduce(
        (acc, capability) => {
            return {
                ...acc,
                [capability]: allAppIntegrations.filter((integration) => integration.capabilities.includes(capability)),
            };
        },
        {} as Record<IntegrationCapability, Integration[]>,
    );

    const stepConfigConnectedIntegrations = useSelector((state: RootState) =>
        selectConnectedIntegrationsForStepConfig(state, appId, stepConfig.id),
    );

    const stepConfigConnectedIntegrationsByCapability = AllIntegrationCapabilities.reduce(
        (acc, capability) => {
            return {
                ...acc,
                [capability]: stepConfigConnectedIntegrations.filter((integration) =>
                    integration.capabilities.includes(capability),
                ),
            };
        },
        {} as Record<IntegrationCapability, Integration[]>,
    );

    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));

    const addIntegration = (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => {
        const request: CreateIntegrationRequest = {
            orgId,
            appId,
            integrationId: integrationType,
            data,
        };

        const authType = authTypeForIntegrationType(integrationType);

        if (authType) {
            dispatch(
                tryCreateIntegrationRequest(authType, {
                    ...request,
                    isQuickStartCI: false,
                }),
            );
        } else {
            globalMessage.error('Unrecognized auth type for integration type');
        }
    };

    const initialValuesForIntegrationSettings: IntegrationSettingsByArchetype<typeof stepConfig.archetype> =
        getIntegrationSettingsByArchetype(stepConfig);

    const initialValues: IntegrationsFormValues = {
        integrationInstallationIdByCapability: {},
        integrationSpecificSettings: initialValuesForIntegrationSettings,
    };

    for (const [capability, integrations] of Object.entries<Integration[]>(
        stepConfigConnectedIntegrationsByCapability,
    )) {
        integrations.forEach((integration) => {
            const key = capability;
            initialValues.integrationInstallationIdByCapability[key] = integration.installationId;
        });
    }

    const allConnectedIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));

    const integrationSpecificSettingsFields = getIntegrationSpecificSettingsFields({
        archetype: stepConfig.archetype,
        allConnectedIntegrations,
        isEditable: !isDisabled,
    });

    const integrationInstallationIdByCapability = Form.useWatch(
        ['integrationInstallationIdByCapability'],
        extendedFormObject.form,
    );

    const renderIntegrationSection = (title: string, capabilities: IntegrationCapability[], isRequired = false) => {
        if (!capabilities || capabilities.length === 0) return null;

        return (
            <Form.Item shouldUpdate noStyle>
                {() => {
                    return (
                        <>
                            <Text
                                style={{
                                    ...titleStyle,
                                    fontWeight: 400,
                                    fontSize: 14,
                                    marginBottom: 16,
                                    display: 'block',
                                }}
                            >
                                {title}
                            </Text>

                            {capabilities.map((capability) => {
                                const integrations = allAppIntegrationsByCapability[capability];

                                const options: SelectProps['options'] = integrations.map((integration) => {
                                    const iconAndAlt = iconAndAltForIntegrationType(integration.id);
                                    const integrationIdentifiers = getIntegrationIdentifiers(integration);
                                    const integrationAlias =
                                        typeof integration.alias === 'string' && integration.alias.trim().length > 0 ?
                                            integration.alias.trim()
                                        :   null;
                                    return {
                                        value: integration.installationId,
                                        label: (
                                            <Space
                                                split={
                                                    <Divider
                                                        type="vertical"
                                                        style={{
                                                            backgroundColor: antGrey.grey5,
                                                            height: 18,
                                                        }}
                                                    />
                                                }
                                            >
                                                <Space align="center" size={8}>
                                                    <img
                                                        src={iconAndAlt[0]}
                                                        alt={iconAndAlt[1]}
                                                        width={20}
                                                        height={20}
                                                    />
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: integrationAlias ? '6px' : undefined,
                                                        }}
                                                    >
                                                        <Text>{integration.name}</Text>
                                                        {integrationAlias && (
                                                            <Text type="secondary">{integrationAlias}</Text>
                                                        )}
                                                    </div>
                                                </Space>
                                                {integrationIdentifiers.map((identifier) => (
                                                    <Text>{identifier}</Text>
                                                ))}
                                            </Space>
                                        ),
                                    };
                                });

                                const allSupportedIntegrations = allSupportedIntegrationTypesForCapability(
                                    capability,
                                    isRunwayOrg,
                                    orgId,
                                    true,
                                );

                                const connectedIntegrationTypes = integrations.map((integration) => integration.id);

                                const currentIntegrationInstallationId =
                                    integrationInstallationIdByCapability?.[capability];

                                return (
                                    <>
                                        <Form.Item
                                            name={['integrationInstallationIdByCapability', capability]}
                                            label={displayNameForCapability(capability)}
                                            required={isRequired}
                                        >
                                            <Select
                                                disabled={isDisabled}
                                                options={options}
                                                placeholder="Please select"
                                                allowClear={!isRequired}
                                            />
                                        </Form.Item>

                                        {integrationSpecificSettingsFields[currentIntegrationInstallationId] && (
                                            <>
                                                {renderFormFields(
                                                    integrationSpecificSettingsFields[currentIntegrationInstallationId],
                                                )}
                                            </>
                                        )}

                                        <Collapse defaultActiveKey={['1']} ghost style={{ marginBottom: 24 }}>
                                            <Collapse.Panel
                                                header={`Connect ${integrations.length > 0 ? 'another' : 'a'} ${displayNameForCapability(capability)} integration`}
                                                key={capability}
                                            >
                                                <PlanRestrictedFeatureWrapper integrationCapability={capability}>
                                                    {(isDisabled) => (
                                                        <IntegrationsForCapabilityComponent
                                                            integrationCapability={capability}
                                                            allSupportedIntegrationsForCapability={
                                                                allSupportedIntegrations
                                                            }
                                                            connectedIntegrationsForCapability={
                                                                connectedIntegrationTypes
                                                            }
                                                            isDisabled={isDisabled}
                                                            orgId={orgId}
                                                            appId={appId}
                                                            shouldUseSmallGrid
                                                            integrationIsLoading={addIntegrationIsLoading}
                                                            addIntegrationSuccess={addIntegrationSucceeded}
                                                            addIntegration={addIntegration}
                                                        />
                                                    )}
                                                </PlanRestrictedFeatureWrapper>
                                            </Collapse.Panel>
                                        </Collapse>
                                    </>
                                );
                            })}
                        </>
                    );
                }}
            </Form.Item>
        );
    };

    return (
        <ExtendedForm
            hidden={hidden}
            extendedFormObject={extendedFormObject}
            layout="vertical"
            preserve={false}
            initialValues={initialValues}
            disabled={isDisabled}
        >
            {renderIntegrationSection('Required integrations', stepConfig.requiredCapabilities, true)}

            {stepConfig.optionalCapabilities && stepConfig.optionalCapabilities.length > 0 && (
                <>
                    <Divider />
                    {renderIntegrationSection('Optional integrations', stepConfig.optionalCapabilities)}
                </>
            )}
        </ExtendedForm>
    );
};
const getIntegrationIdentifiers: (integration: Integration) => string[] = (integration) => {
    const fallback = [integration.installationId];
    const normalize = (ids: (string | undefined | null)[] | undefined): string[] => {
        if (ids == null) return fallback;
        const filtered = ids.map((id) => (typeof id === 'string' ? id.trim() : '')).filter((id) => id.length > 0);
        return filtered.length > 0 ? filtered : fallback;
    };

    let candidateIds: (string | undefined | null)[] | undefined;
    switch (integration.id) {
        case IntegrationType.GitHubVCSDistribution:
        case IntegrationType.GitHubVCS:
        case IntegrationType.GitHubCI:
        case IntegrationType.GitHubIT:
            candidateIds = integration.data?.repoName ? [integration.data.repoName] : undefined;
            break;
        case IntegrationType.Bitbucket:
        case IntegrationType.BitbucketCI:
            candidateIds = integration.data?.selectedRepo?.name ? [integration.data.selectedRepo.name] : undefined;
            break;
        case IntegrationType.GitLabVCS:
        case IntegrationType.GitLabCI:
            candidateIds =
                integration.data?.project?.displayableName ? [integration.data.project.displayableName] : undefined;
            break;
        case IntegrationType.TestFlight:
        case IntegrationType.Apple:
            candidateIds = integration.data?.bundleId ? [integration.data.bundleId] : undefined;
            break;
        case IntegrationType.AzureCI:
        case IntegrationType.AzureIT:
        case IntegrationType.AzureVCS:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Codemagic:
            candidateIds = integration.data?.appName ? [integration.data.appName] : undefined;
            break;
        case IntegrationType.Bitrise:
            candidateIds = integration.data?.appTitle ? [integration.data.appTitle] : undefined;
            break;
        case IntegrationType.Buildkite:
            candidateIds = integration.data?.org?.name ? [integration.data.org.name] : undefined;
            break;
        case IntegrationType.Bugsnag:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Sentry:
            candidateIds =
                integration.data?.selectedProject?.displayableName ?
                    [integration.data.selectedProject.displayableName]
                :   undefined;
            break;
        case IntegrationType.EmbraceStabilityMonitoring:
            candidateIds =
                integration.data?.selectedProdApp?.name ? [integration.data.selectedProdApp.name] : undefined;
            break;
        case IntegrationType.TestRail:
        case IntegrationType.BrowserStack:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Qase:
            candidateIds =
                integration.data?.selectedProject?.title ? [integration.data.selectedProject.title] : undefined;
            break;
        case IntegrationType.Jenkins:
            candidateIds = integration.data?.workflowName ? [integration.data.workflowName] : undefined;
            break;
        case IntegrationType.Jira:
            candidateIds = integration.data?.selectedProjects?.map((project) => project.name);
            break;
        case IntegrationType.Asana:
        case IntegrationType.Pivotal:
            candidateIds = integration.data?.selectedProjects?.map((project) => project.name);
            break;
        case IntegrationType.Shortcut:
            candidateIds = integration.data?.selectedGroups?.map((group) => group.name);
            break;
        case IntegrationType.Slack:
            candidateIds = integration.data?.primaryChannels?.map((channel) => channel.name);
            break;
        case IntegrationType.CircleCI:
            candidateIds = integration.data?.repoName ? [integration.data.repoName] : undefined;
            break;
        case IntegrationType.Amazon:
            candidateIds = integration.data?.selectedAppId ? [integration.data.selectedAppId] : undefined;
            break;
        case IntegrationType.Google:
        case IntegrationType.GoogleBeta:
            candidateIds = integration.data?.packageName ? [integration.data.packageName] : undefined;
            break;
        case IntegrationType.HuaweiAppGallery:
            candidateIds = integration.data?.appId ? [integration.data.appId] : undefined;
            break;
        case IntegrationType.Linear:
            candidateIds = integration.data?.selectedTeams?.map((team) => team.name);
            break;
        case IntegrationType.TravisCI:
            candidateIds = integration.data?.repoSlug ? [integration.data.repoSlug] : undefined;
            break;
        case IntegrationType.Firebase:
        case IntegrationType.FirebaseBeta:
            candidateIds = integration.data?.projectId ? [integration.data.projectId] : undefined;
            break;
        case IntegrationType.Xray:
        case IntegrationType.Amplitude:
        case IntegrationType.DatadogAnalytics:
        case IntegrationType.GoogleAnalytics:
        case IntegrationType.MixpanelAnalytics:
            candidateIds = [];
            break;
        case IntegrationType.AppCenterCI:
        case IntegrationType.AppCenterBeta:
            candidateIds = integration.data?.selectedApp?.appSlug ? [integration.data.selectedApp.appSlug] : undefined;
            break;
        case IntegrationType.Optimizely:
        case IntegrationType.LaunchDarkly:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.Statsig:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.GenericCI:
            candidateIds = integration.data?.workflowName ? [integration.data.workflowName] : undefined;
            break;
        case IntegrationType.AndroidVitals:
            candidateIds = integration.data?.appId ? [integration.data.appId] : undefined;
            break;
        case IntegrationType.XcodeCloud:
            candidateIds = integration.data?.productName ? [integration.data.productName] : undefined;
            break;
        case IntegrationType.Monday:
            candidateIds = integration.data?.selectedBoards?.map((board) => board.name);
            break;
        case IntegrationType.RunwayBuildDistroBeta:
            candidateIds = integration.data?.selectedBucket?.name ? [integration.data.selectedBucket.name] : undefined;
            break;
        case IntegrationType.MicrosoftTeams:
            candidateIds = integration.data?.primaryChannels?.map((channel) => channel.displayName);
            break;
        case IntegrationType.DatadogStabilityMonitoring:
            candidateIds = integration.data?.prodEnvironment ? [integration.data.prodEnvironment] : undefined;
            break;
        case IntegrationType.Crowdin:
        case IntegrationType.Lokalise:
            candidateIds =
                integration.data?.selectedProject?.name ? [integration.data.selectedProject.name] : undefined;
            break;
        case IntegrationType.PagerDutyScheduling:
        case IntegrationType.OpsgenieScheduling:
        case IntegrationType.JiraServiceManagementScheduling:
        case IntegrationType.IncidentIoScheduling:
        case IntegrationType.RootlyScheduling:
            candidateIds =
                integration.data?.selectedSchedule?.name ? [integration.data.selectedSchedule.name] : undefined;
            break;
        case IntegrationType.NewRelicStabilityMonitoring:
            candidateIds =
                integration.data?.prodMobileApplication?.name ?
                    [integration.data.prodMobileApplication.name]
                :   undefined;
            break;
        case IntegrationType.NewRelicAnalytics:
            candidateIds = integration.data?.application?.name ? [integration.data.application.name] : undefined;
            break;
        case IntegrationType.Samsung:
            candidateIds = integration.data?.appId ? [integration.data.appId] : undefined;
            break;
        case IntegrationType.DynatraceStabilityMonitoring:
            candidateIds = integration.data?.prodRumApp?.name ? [integration.data.prodRumApp.name] : undefined;
            break;
        case IntegrationType.CustomAnalytics:
        case IntegrationType.CustomCI:
            candidateIds = integration.data?.integrationId ? [integration.data.integrationId] : undefined;
            break;
        case IntegrationType.GoogleCalendar:
        case IntegrationType.GooglePersonalCalendar:
            candidateIds = integration.data?.calendarName ? [integration.data.calendarName] : undefined;
            break;
        default:
            candidateIds = [];
            break;
    }
    return normalize(candidateIds);
};
